<template>
  <div class="an-root">
    <!-- ── Top bar ─────────────────────────────────────────────────────── -->
    <header class="an-topbar">
      <div class="brand">
        <button class="back-btn" @click="goBack" title="Back to dashboard"><v-icon size="20" color="#14663a">mdi-arrow-left</v-icon></button>
        <div class="brand-logo-box"><img :src="wtiLogo" class="brand-logo" alt="WTI" /></div>
        <div class="brand-text">
          <div class="brand-title">Performance Analytics</div>
          <div class="brand-sub">Effectiveness of employees, divisions &amp; managers</div>
        </div>
      </div>
      <div class="topbar-actions">
        <span class="fy-chip" v-if="financialYear">FY {{ financialYear }}</span>
        <button class="btn ghost" :disabled="loading" @click="fetchData"><v-icon size="17" :class="{ spin: loading }">mdi-refresh</v-icon><span>Refresh</span></button>
        <button class="btn solid" @click="logout"><v-icon size="17">mdi-logout</v-icon><span>Logout</span></button>
      </div>
    </header>

    <div v-if="loading" class="loader-wrap"><v-progress-circular indeterminate color="#14663a" size="52" width="4" /></div>

    <main v-else class="an-body">
      <div v-if="error" class="banner error"><v-icon size="18" color="#c62828">mdi-alert-circle-outline</v-icon> {{ error }}</div>

      <!-- ── Adoption ──────────────────────────────────────────────────── -->
      <div class="section-eyebrow"><v-icon size="16" color="#14663a">mdi-progress-check</v-icon> Target Process Adoption</div>
      <div v-if="a.adoption.submissionRate < 25" class="insight">
        <v-icon size="18" color="#b56b00">mdi-alert</v-icon>
        <span>Only <b>{{ a.adoption.submissionRate }}%</b> of targets have been submitted and <b>{{ a.adoption.notStarted }}</b> of {{ a.adoption.employees }} employees haven't submitted any — target scores below are provisional until the KPI process is actively used.</span>
      </div>
      <section class="funnel">
        <div class="fn-step"><div class="fn-v">{{ num(a.adoption.assigned) }}</div><div class="fn-l">Targets assigned</div></div>
        <v-icon size="20" color="#c9d6ce">mdi-chevron-right</v-icon>
        <div class="fn-step"><div class="fn-v">{{ num(a.adoption.submitted) }}</div><div class="fn-l">Submitted <em>{{ a.adoption.submissionRate }}%</em></div></div>
        <v-icon size="20" color="#c9d6ce">mdi-chevron-right</v-icon>
        <div class="fn-step"><div class="fn-v">{{ num(a.adoption.approved) }}</div><div class="fn-l">Approved <em>{{ a.adoption.approvalRate }}%</em></div></div>
        <div class="fn-step warn"><div class="fn-v">{{ num(a.adoption.notStarted) }}</div><div class="fn-l">Not started (0 submitted)</div></div>
      </section>

      <!-- ── Division scorecards ───────────────────────────────────────── -->
      <div class="section-eyebrow"><v-icon size="16" color="#14663a">mdi-folder-multiple</v-icon> Division Scorecards</div>
      <section class="card table-card">
        <table class="an-table">
          <thead><tr><th>Division</th><th class="r">Staff</th><th class="r">Targets</th><th>Effectiveness</th><th class="r">Attendance</th><th class="r">Submission</th><th class="r">Not Met</th><th class="r">Exits</th></tr></thead>
          <tbody>
            <tr v-for="d in a.divisions" :key="d.division">
              <td class="strong">{{ d.division }}</td>
              <td class="r">{{ d.staff }}</td>
              <td class="r">{{ d.targets }}</td>
              <td><div class="score-cell"><div class="score-bar"><div class="score-fill" :style="{ width: d.score + '%', background: scoreColor(d.score) }"></div></div><span class="score-num">{{ d.score }}</span></div></td>
              <td class="r">{{ d.avgAttendance }}%</td>
              <td class="r">{{ d.submissionRate }}%</td>
              <td class="r"><span :class="d.notMet > 0 ? 'bad' : 'dim'">{{ d.notMet }}</span></td>
              <td class="r"><span :class="d.exits > 0 ? 'bad' : 'dim'">{{ d.exits }}</span></td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- ── Manager effectiveness / approval bottlenecks ──────────────── -->
      <div class="section-eyebrow"><v-icon size="16" color="#14663a">mdi-account-tie</v-icon> Manager Effectiveness &amp; Approval Bottlenecks</div>
      <section class="card table-card">
        <table class="an-table">
          <thead><tr><th>Manager</th><th class="r">Team</th><th>Team effectiveness</th><th class="r">Pending approvals</th><th class="r">Oldest pending</th></tr></thead>
          <tbody>
            <tr v-for="m in a.managers" :key="m.id">
              <td class="strong">{{ m.name || '—' }}</td>
              <td class="r">{{ m.teamSize }}</td>
              <td><div class="score-cell"><div class="score-bar"><div class="score-fill" :style="{ width: m.teamAvgScore + '%', background: scoreColor(m.teamAvgScore) }"></div></div><span class="score-num">{{ m.teamAvgScore }}</span></div></td>
              <td class="r"><span :class="m.pendingApprovals > 0 ? 'warn-txt' : 'dim'">{{ m.pendingApprovals }}</span></td>
              <td class="r"><span :class="m.oldestPendingDays > 0 ? 'warn-txt' : 'dim'">{{ m.oldestPendingDays ? m.oldestPendingDays + 'd' : '—' }}</span></td>
            </tr>
            <tr v-if="!a.managers.length"><td colspan="5" class="dim center">No reporting-manager data.</td></tr>
          </tbody>
        </table>
      </section>

      <!-- ── Employee effectiveness leaderboard ────────────────────────── -->
      <div class="section-eyebrow"><v-icon size="16" color="#14663a">mdi-trophy-outline</v-icon> Employee Effectiveness</div>
      <section class="controls">
        <div class="view-toggle">
          <button :class="['vt', view === 'top' && 'on']" @click="view = 'top'">Top</button>
          <button :class="['vt', view === 'attention' && 'on']" @click="view = 'attention'">Needs attention</button>
          <button :class="['vt', view === 'all' && 'on']" @click="view = 'all'">All</button>
        </div>
        <div class="filters">
          <div class="search-box"><v-icon size="17" color="#8a978f">mdi-magnify</v-icon><input v-model="search" type="text" placeholder="Search name or ID" /></div>
          <select v-model="divisionFilter" class="sel"><option value="">All divisions</option><option v-for="d in divisionNames" :key="d" :value="d">{{ d }}</option></select>
        </div>
      </section>
      <section class="card table-card">
        <table class="an-table">
          <thead><tr><th>#</th><th>Name</th><th>Division</th><th>Effectiveness</th><th class="r">Achieve</th><th class="r">On-time</th><th class="r">Attend</th><th class="r">Submit</th><th class="r">PMR</th></tr></thead>
          <tbody>
            <tr v-for="(e, i) in shownEmployees" :key="e.id">
              <td class="dim">{{ i + 1 }}</td>
              <td class="strong">{{ e.name }} <span class="dim">{{ e.employeeId ? '· ' + e.employeeId : '' }}</span><span v-if="e.onProbation" class="prob-tag">probation</span></td>
              <td><span class="proj-chip">{{ e.division }}</span></td>
              <td><div class="score-cell"><div class="score-bar"><div class="score-fill" :style="{ width: e.score + '%', background: scoreColor(e.score) }"></div></div><span class="score-num">{{ e.score }}</span></div></td>
              <td class="r">{{ e.metPercent }}%</td>
              <td class="r">{{ e.onTimeRate == null ? '—' : e.onTimeRate + '%' }}</td>
              <td class="r">{{ e.attendancePct }}%</td>
              <td class="r">{{ e.submissionRate }}%</td>
              <td class="r">{{ e.pmrRating == null ? '—' : e.pmrRating }}</td>
            </tr>
            <tr v-if="!shownEmployees.length"><td colspan="9" class="dim center">No employees match.</td></tr>
          </tbody>
        </table>
      </section>

      <!-- ── Probation watch ───────────────────────────────────────────── -->
      <div class="section-eyebrow"><v-icon size="16" color="#e08600">mdi-account-clock</v-icon> Probation Watch <span class="eye-sub">confirm / extend / exit decisions</span></div>
      <section class="card table-card">
        <table class="an-table">
          <thead><tr><th>Name</th><th>Division</th><th class="r">Tenure</th><th>Effectiveness</th><th class="r">Attendance</th><th class="r">Targets met %</th></tr></thead>
          <tbody>
            <tr v-for="p in a.probationWatch" :key="p.id">
              <td class="strong">{{ p.name }} <span class="dim">{{ p.employeeId ? '· ' + p.employeeId : '' }}</span></td>
              <td><span class="proj-chip">{{ p.division }}</span></td>
              <td class="r">{{ p.tenureYears == null ? '—' : p.tenureYears + 'y' }}</td>
              <td><div class="score-cell"><div class="score-bar"><div class="score-fill" :style="{ width: p.score + '%', background: scoreColor(p.score) }"></div></div><span class="score-num">{{ p.score }}</span></div></td>
              <td class="r">{{ p.attendancePct }}%</td>
              <td class="r">{{ p.metPercent }}%</td>
            </tr>
            <tr v-if="!a.probationWatch.length"><td colspan="6" class="dim center">No employees on probation.</td></tr>
          </tbody>
        </table>
      </section>
    </main>
  </div>
</template>

<script>
import axios from "axios";

function empty() {
  return {
    adoption: { assigned: 0, submitted: 0, approved: 0, notStarted: 0, submissionRate: 0, approvalRate: 0, employees: 0 },
    workingDaysElapsed: 0, employees: [], divisions: [], managers: [], probationWatch: [],
  };
}

export default {
  name: "CeoAnalytics",
  data() {
    return {
      loading: true, error: "", financialYear: "", pmrAvailable: false,
      a: empty(), view: "top", search: "", divisionFilter: "",
      wtiLogo: require("@/assets/images/wtilogo.png"),
    };
  },
  computed: {
    divisionNames() { return Array.from(new Set((this.a.employees || []).map((e) => e.division))).sort(); },
    filteredEmployees() {
      const q = this.search.trim().toLowerCase();
      return this.a.employees.filter((e) => {
        if (this.divisionFilter && e.division !== this.divisionFilter) return false;
        if (q && !((e.name || "").toLowerCase().includes(q) || String(e.employeeId || "").toLowerCase().includes(q))) return false;
        return true;
      });
    },
    shownEmployees() {
      const list = this.filteredEmployees;
      if (this.view === "top") return list.slice(0, 10);
      if (this.view === "attention") return list.slice().reverse().slice(0, 10);
      return list;
    },
  },
  mounted() { this.fetchData(); },
  methods: {
    authHeaders() { return { "x-auth-token": localStorage.getItem("token") }; },
    async fetchData() {
      this.loading = true; this.error = "";
      try {
        const res = await axios({ method: "GET", url: "dashboard/ceo/analytics", headers: this.authHeaders() });
        if (res.data && res.data.status) {
          this.a = Object.assign(empty(), res.data.data);
          this.financialYear = res.data.financialYear || "";
          this.pmrAvailable = !!res.data.pmrAvailable;
        } else { this.error = (res.data && res.data.msg) || "Failed to load analytics."; }
      } catch (e) { this.error = "Unable to reach the server."; }
      finally { this.loading = false; }
    },
    scoreColor(s) { return s >= 70 ? "#2e7d32" : s >= 40 ? "#e08600" : "#c62828"; },
    goBack() { this.$router.push("/ceo-dashboard"); },
    logout() { this.$store.commit("logoutUser"); },
    num(n) { return Number(n || 0).toLocaleString("en-IN"); },
  },
};
</script>

<style scoped>
.an-root { min-height: 100vh; width: 100%; background: linear-gradient(180deg, #f3f7f4 0%, #e9f0eb 100%); font-family: "Poppins", "Segoe UI", system-ui, sans-serif; color: #1b2320; }
.an-topbar { position: sticky; top: 0; z-index: 20; display: flex; align-items: center; justify-content: space-between; padding: 12px 26px; background: rgba(255,255,255,0.94); backdrop-filter: saturate(180%) blur(12px); border-bottom: 1px solid rgba(20,102,58,0.12); }
.brand { display: flex; align-items: center; gap: 12px; }
.back-btn { background: #fff; border: 1px solid rgba(20,102,58,0.2); border-radius: 10px; width: 38px; height: 38px; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.back-btn:hover { background: #eef6f0; }
.brand-logo-box { width: 46px; height: 46px; border-radius: 12px; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #14663a, #2e7d32); box-shadow: 0 6px 16px rgba(20,102,58,0.3); padding: 6px; }
.brand-logo { max-width: 100%; max-height: 100%; object-fit: contain; }
.brand-title { font-size: 17px; font-weight: 700; }
.brand-sub { font-size: 11.5px; color: #6b7a72; margin-top: -2px; }
.topbar-actions { display: flex; align-items: center; gap: 12px; }
.fy-chip { font-size: 12px; font-weight: 700; color: #14663a; background: #e5f1ea; border: 1px solid #cfe5d8; padding: 5px 12px; border-radius: 20px; }
.btn { display: inline-flex; align-items: center; gap: 7px; border: none; cursor: pointer; font-size: 13px; font-weight: 600; padding: 8px 14px; border-radius: 10px; font-family: inherit; }
.btn.ghost { background: #fff; color: #14663a; border: 1px solid rgba(20,102,58,0.2); }
.btn.solid { background: linear-gradient(135deg, #14663a, #2e7d32); color: #fff; }
.spin { animation: spin 0.9s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }
.loader-wrap { display: flex; justify-content: center; align-items: center; min-height: 70vh; }
.an-body { max-width: 1280px; margin: 0 auto; padding: 22px 26px 44px; }
.banner.error { display: flex; gap: 8px; background: #fdecec; color: #c62828; border: 1px solid #f5c6c6; padding: 10px 14px; border-radius: 12px; margin-bottom: 16px; font-size: 13px; }
.section-eyebrow { display: flex; align-items: center; gap: 7px; font-size: 12.5px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; color: #14663a; margin: 22px 2px 12px; }
.eye-sub { text-transform: none; letter-spacing: 0; font-weight: 500; color: #9aa8a1; font-size: 11.5px; }
.insight { display: flex; gap: 8px; align-items: flex-start; background: #fdf6ea; border: 1px solid #f2ddb8; color: #7a5a1e; padding: 11px 14px; border-radius: 12px; font-size: 13px; margin-bottom: 14px; }
.insight b { color: #5a4110; }

.funnel { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; background: #fff; border: 1px solid #edf2ee; border-radius: 16px; padding: 18px 20px; box-shadow: 0 3px 14px rgba(20,40,30,0.05); }
.fn-step { flex: 1; min-width: 120px; text-align: center; }
.fn-step.warn { background: #fdf3e3; border-radius: 12px; padding: 6px; }
.fn-v { font-size: 26px; font-weight: 700; color: #14432a; }
.fn-l { font-size: 12px; color: #6b7a72; margin-top: 2px; } .fn-l em { font-style: normal; color: #14663a; font-weight: 700; }

.card { background: #fff; border-radius: 16px; box-shadow: 0 3px 14px rgba(20,40,30,0.05); border: 1px solid #edf2ee; margin-bottom: 8px; }
.table-card { overflow-x: auto; }
.an-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.an-table thead th { text-align: left; background: #f7faf8; color: #8a978f; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.3px; padding: 10px 14px; white-space: nowrap; }
.an-table td { padding: 10px 14px; border-top: 1px solid #f1f4f2; color: #33403a; }
.an-table .r { text-align: right; }
.strong { font-weight: 600; color: #23302a; }
.dim { color: #9aa8a1; }
.bad { color: #c62828; font-weight: 700; }
.warn-txt { color: #b56b00; font-weight: 700; }
.center { text-align: center; }
.proj-chip { font-size: 11px; background: #eef6f0; color: #14663a; padding: 3px 9px; border-radius: 12px; white-space: nowrap; }
.prob-tag { font-size: 10px; font-weight: 700; color: #b56b00; background: #fbf1e2; padding: 1px 7px; border-radius: 10px; margin-left: 6px; }
.score-cell { display: flex; align-items: center; gap: 8px; min-width: 120px; }
.score-bar { flex: 1; height: 8px; background: #eef2f0; border-radius: 5px; overflow: hidden; }
.score-fill { height: 100%; border-radius: 5px; }
.score-num { font-weight: 700; color: #23302a; font-size: 12.5px; width: 34px; text-align: right; }

.controls { display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap; margin-bottom: 10px; }
.view-toggle { display: flex; background: #fff; border: 1px solid #dcebe1; border-radius: 10px; overflow: hidden; }
.vt { border: none; background: transparent; cursor: pointer; font-family: inherit; font-size: 13px; font-weight: 600; color: #5a6b62; padding: 9px 16px; }
.vt.on { background: #14663a; color: #fff; }
.filters { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.search-box { display: flex; align-items: center; gap: 6px; background: #fff; border: 1px solid #dcebe1; border-radius: 10px; padding: 7px 12px; }
.search-box input { border: none; outline: none; font-family: inherit; font-size: 13px; background: transparent; width: 170px; color: #23302a; }
.sel { font-family: inherit; font-size: 13px; color: #23302a; background: #fff; border: 1px solid #dcebe1; border-radius: 10px; padding: 8px 12px; cursor: pointer; }

@media (max-width: 800px) { .an-body { padding: 16px; } .funnel { flex-direction: column; } .controls { flex-direction: column; align-items: stretch; } }
</style>
